import {
  buildProfileShippingRatesFromFormClass,
  validateProfileShippingClass,
} from '../util/profileShippingRates';

/**
 * R2R commercial seller helpers for profile policies and Community Sponsor.
 */

export const COMMERCIAL_USER_TYPES = ['commercial_sponsor', 'commercial_non_sponsor'];
export const COMMUNITY_SPONSOR_USER_TYPE = 'commercial_sponsor';

/** Default banner for commercial sellers who have not uploaded a custom one. */
export const DEFAULT_PROFILE_BANNER_PATH = '/static/profile/default-coral-banner.jpg';

/** Reef2Reef mark used next to “Community Sponsor” on public profiles. */
export const COMMUNITY_SPONSOR_BADGE_PATH = '/static/profile/community-sponsor-badge.png';

/**
 * @param {Object|null|undefined} user Marketplace user or currentUser entity
 * @returns {string|null}
 */
export const getUserTypeId = user => {
  const userType = user?.attributes?.profile?.publicData?.userType;
  return typeof userType === 'string' && userType.length > 0 ? userType : null;
};

/**
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const isCommercialSeller = user => COMMERCIAL_USER_TYPES.includes(getUserTypeId(user));

/**
 * Automatic trust label for commercial sponsors (not a settings toggle).
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const isCommunitySponsor = user => getUserTypeId(user) === COMMUNITY_SPONSOR_USER_TYPE;

/**
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const hasShippingPolicy = user => {
  const text = user?.attributes?.profile?.publicData?.sellerShippingPolicy;
  return !!String(text || '').trim();
};

/**
 * LAG badge/modal requires both the flag and non-empty DOA text.
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const hasLiveArrivalGuarantee = user => {
  const publicData = user?.attributes?.profile?.publicData || {};
  return (
    publicData.sellerLiveArrivalGuarantee === true &&
    !!String(publicData.sellerDoaReturnPolicy || '').trim()
  );
};

/**
 * Console-hosted listing field key for label "DOA & Return Policy*"
 * (`/listings/listing-fields.json`). Form name is `pub_${key}`.
 */
export const DOA_RETURN_POLICY_LISTING_FIELD_KEY = 'DOA_policy';

/**
 * Final Form field name for the listing DOA & Return Policy extended data field.
 * @returns {string}
 */
export const getDoaReturnPolicyFormName = () => `pub_${DOA_RETURN_POLICY_LISTING_FIELD_KEY}`;

/**
 * @param {Array<Object>|null|undefined} listingFieldsConfig
 * @returns {Object|null}
 */
export const findDoaReturnPolicyFieldConfig = listingFieldsConfig =>
  (listingFieldsConfig || []).find(f => f?.key === DOA_RETURN_POLICY_LISTING_FIELD_KEY) || null;

/**
 * @param {Object|null|undefined} user
 * @returns {string|null} trimmed DOA text when LAG is usable; otherwise null
 */
export const getProfileDoaPolicyText = user => {
  if (!hasLiveArrivalGuarantee(user)) {
    return null;
  }
  return String(user.attributes.profile.publicData.sellerDoaReturnPolicy).trim();
};

/**
 * Whether the listing Details form should show "Copy from profile DOA policy".
 * Requires commercial seller + LAG on + non-empty DOA text.
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const canCopyDoaFromProfile = user =>
  isCommercialSeller(user) && getProfileDoaPolicyText(user) != null;

/**
 * Banner URL for public profile: custom upload, else default coral for commercial, else null.
 * @param {Object|null|undefined} user
 * @returns {string|null}
 */
export const getProfileBannerUrl = user => {
  if (!isCommercialSeller(user)) {
    return null;
  }
  const custom = user?.attributes?.profile?.publicData?.profileBannerUrl;
  const trimmed = typeof custom === 'string' ? custom.trim() : '';
  return trimmed || DEFAULT_PROFILE_BANNER_PATH;
};

/**
 * @param {boolean} enabled
 * @param {{ amount?: number }|null|undefined} moneyValue
 * @returns {number|null}
 */
const pickThresholdSubunits = (enabled, moneyValue) => {
  if (enabled !== true) {
    return null;
  }
  const amount = moneyValue?.amount;
  return typeof amount === 'number' && Number.isInteger(amount) ? amount : null;
};

/**
 * @param {boolean} enabled
 * @param {{ amount?: number }|null|undefined} moneyValue
 * @param {string} errorKey
 * @param {string} requiredMessage
 * @returns {Object|null}
 */
const validateFreeShippingThreshold = (enabled, moneyValue, errorKey, requiredMessage) => {
  if (enabled !== true) {
    return null;
  }
  const amount = moneyValue?.amount;
  if (typeof amount !== 'number' || !Number.isInteger(amount) || amount <= 0) {
    return { [errorKey]: requiredMessage };
  }
  return null;
};

/**
 * Pick location + commercial seller policy fields for a profile update publicData patch.
 * Only patched keys — Marketplace API merges publicData server-side.
 *
 * @param {Object} values form values
 * @param {{ isCommercial: boolean }} options
 * @returns {Object}
 */
export const pickSellerProfilePublicData = (values, { isCommercial }) => {
  const profileLocation = String(values?.profileLocation || '').trim();
  const patch = {
    profileLocation: profileLocation || null,
  };

  if (!isCommercial) {
    return {
      ...patch,
      freeShippingLiveEnabled: false,
      freeShippingLiveThresholdSubunits: null,
      freeShippingDryEnabled: false,
      freeShippingDryThresholdSubunits: null,
      shippingRatesLiveEnabled: false,
      shippingRatesLive: null,
      shippingRatesDryEnabled: false,
      shippingRatesDry: null,
    };
  }

  const lagOn = values?.sellerLiveArrivalGuarantee === true;
  const shippingPolicy = String(values?.sellerShippingPolicy || '').trim();
  const doa = String(values?.sellerDoaReturnPolicy || '').trim();
  const freeShippingLiveEnabled = values?.freeShippingLiveEnabled === true;
  const freeShippingDryEnabled = values?.freeShippingDryEnabled === true;
  const live = buildProfileShippingRatesFromFormClass({
    enabled: values?.shippingRatesLiveEnabled,
    first: values?.shippingRatesLiveFirst,
    additional: values?.shippingRatesLiveAdditional,
    zones: values?.shippingRatesLiveZones,
  });
  const dry = buildProfileShippingRatesFromFormClass({
    enabled: values?.shippingRatesDryEnabled,
    first: values?.shippingRatesDryFirst,
    additional: values?.shippingRatesDryAdditional,
    zones: values?.shippingRatesDryZones,
  });

  return {
    ...patch,
    sellerShippingPolicy: shippingPolicy || null,
    sellerLiveArrivalGuarantee: lagOn,
    sellerDoaReturnPolicy: lagOn ? doa || null : null,
    freeShippingLiveEnabled,
    freeShippingLiveThresholdSubunits: pickThresholdSubunits(
      freeShippingLiveEnabled,
      values?.freeShippingLiveThreshold
    ),
    freeShippingDryEnabled,
    freeShippingDryThresholdSubunits: pickThresholdSubunits(
      freeShippingDryEnabled,
      values?.freeShippingDryThreshold
    ),
    shippingRatesLiveEnabled: live.enabled,
    shippingRatesLive: live.rates,
    shippingRatesDryEnabled: dry.enabled,
    shippingRatesDry: dry.rates,
  };
};

/**
 * Final Form validate for commercial seller policy fields.
 * @param {Object} values
 * @param {{
 *   isCommercial: boolean,
 *   doaRequiredMessage: string,
 *   freeShippingThresholdRequiredMessage?: string,
 *   shippingFeeRequiredMessage?: string,
 * }} options
 * @returns {Object} errors
 */
export const validateSellerProfileForm = (
  values,
  {
    isCommercial,
    doaRequiredMessage,
    freeShippingThresholdRequiredMessage,
    shippingFeeRequiredMessage,
  }
) => {
  if (!isCommercial) {
    return {};
  }

  const errors = {};

  if (
    values?.sellerLiveArrivalGuarantee === true &&
    !String(values?.sellerDoaReturnPolicy || '').trim()
  ) {
    errors.sellerDoaReturnPolicy = doaRequiredMessage;
  }

  const thresholdMessage = freeShippingThresholdRequiredMessage || '';
  const liveThresholdError = validateFreeShippingThreshold(
    values?.freeShippingLiveEnabled,
    values?.freeShippingLiveThreshold,
    'freeShippingLiveThreshold',
    thresholdMessage
  );
  const dryThresholdError = validateFreeShippingThreshold(
    values?.freeShippingDryEnabled,
    values?.freeShippingDryThreshold,
    'freeShippingDryThreshold',
    thresholdMessage
  );

  const feeMessage = shippingFeeRequiredMessage || '';
  const liveClass = validateProfileShippingClass({
    enabled: values?.shippingRatesLiveEnabled,
    first: values?.shippingRatesLiveFirst,
    additional: values?.shippingRatesLiveAdditional,
    zones: values?.shippingRatesLiveZones,
    feeRequiredMessage: feeMessage,
  });
  if (liveClass.first) errors.shippingRatesLiveFirst = liveClass.first;
  if (liveClass.additional) errors.shippingRatesLiveAdditional = liveClass.additional;
  if (liveClass.zoneErrors) errors.shippingRatesLiveZones = liveClass.zoneErrors;

  const dryClass = validateProfileShippingClass({
    enabled: values?.shippingRatesDryEnabled,
    first: values?.shippingRatesDryFirst,
    additional: values?.shippingRatesDryAdditional,
    zones: values?.shippingRatesDryZones,
    feeRequiredMessage: feeMessage,
  });
  if (dryClass.first) errors.shippingRatesDryFirst = dryClass.first;
  if (dryClass.additional) errors.shippingRatesDryAdditional = dryClass.additional;
  if (dryClass.zoneErrors) errors.shippingRatesDryZones = dryClass.zoneErrors;

  return {
    ...errors,
    ...liveThresholdError,
    ...dryThresholdError,
  };
};

/**
 * Which Seller Trust & Info signals to show on the public profile.
 * Sidebar shows for any commercial seller, or when at least one badge applies.
 *
 * @param {Object|null|undefined} user
 * @returns {{
 *   show: boolean,
 *   communitySponsor: boolean,
 *   shipping: boolean,
 *   liveArrivalGuarantee: boolean,
 * }}
 */
export const getProfileTrustSidebarState = user => {
  const communitySponsor = isCommunitySponsor(user);
  const shipping = hasShippingPolicy(user);
  const liveArrivalGuarantee = hasLiveArrivalGuarantee(user);
  const show = isCommercialSeller(user) || communitySponsor || shipping || liveArrivalGuarantee;
  return {
    show,
    communitySponsor,
    shipping,
    liveArrivalGuarantee,
  };
};

/**
 * Pick best wide image variant URL from an uploaded Sharetribe image entity.
 * @param {Object|null|undefined} uploadedImage
 * @returns {string|null}
 */
export const pickUploadedImageUrl = uploadedImage => {
  const variants = uploadedImage?.attributes?.variants || {};
  const preferred = [
    'scaled-xlarge',
    'scaled-large',
    'scaled-medium',
    'scaled-small',
    'square-small2x',
    'square-small',
  ];
  for (const key of preferred) {
    const url = variants[key]?.url;
    if (typeof url === 'string' && url.trim()) {
      return url.trim();
    }
  }
  return null;
};
